'use client';

import { useEffect, useState } from 'react';

/**
 * Tooltips de la app. Se monta una sola vez en el layout y escucha por delegación
 * cualquier elemento con `data-tip`, así que añadir uno nuevo es solo poner el
 * atributo.
 *
 * Sustituye al atributo `title` nativo porque este tarda cerca de un segundo en
 * aparecer, no se muestra en pantallas táctiles y recorta los textos largos —
 * justo los que explican qué significa cada columna.
 */

const MAX_WIDTH = 320;
const GAP = 10;

interface TipState {
  text: string;
  x: number;
  y: number;
  below: boolean;
}

export default function TooltipLayer() {
  const [tip, setTip] = useState<TipState | null>(null);

  useEffect(() => {
    function show(target: Element) {
      const el = target.closest('[data-tip]');
      if (!el) return;
      const text = el.getAttribute('data-tip');
      if (!text) return;
      const r = el.getBoundingClientRect();
      // Debajo del elemento salvo que no quepa: ahí se muestra encima.
      const below = r.bottom + GAP + 120 < window.innerHeight || r.top < 140;
      setTip({
        text,
        x: Math.min(Math.max(r.left + r.width / 2, MAX_WIDTH / 2 + 8), window.innerWidth - MAX_WIDTH / 2 - 8),
        y: below ? r.bottom + GAP : r.top - GAP,
        below,
      });
    }

    function onOver(e: Event) { show(e.target as Element); }
    function onOut(e: Event) {
      const el = (e.target as Element).closest?.('[data-tip]');
      if (el) setTip(null);
    }
    function onScrollOrKey() { setTip(null); }

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onOver);
    document.addEventListener('focusout', onOut);
    window.addEventListener('scroll', onScrollOrKey, true);
    window.addEventListener('keydown', onScrollOrKey);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onOver);
      document.removeEventListener('focusout', onOut);
      window.removeEventListener('scroll', onScrollOrKey, true);
      window.removeEventListener('keydown', onScrollOrKey);
    };
  }, []);

  if (!tip) return null;

  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x,
        top: tip.y,
        transform: `translate(-50%, ${tip.below ? '0' : '-100%'})`,
        // `max-content` para que ocupe el ancho que pide el texto hasta el tope:
        // sin esto el globo se queda angosto y se estira a lo alto.
        width: 'max-content',
        maxWidth: MAX_WIDTH,
        zIndex: 400,
        pointerEvents: 'none',
        background: 'var(--bg-card-hover)',
        border: '1px solid var(--neon-cyan)',
        boxShadow: '0 0 12px rgba(0, 245, 255, 0.25)',
        borderRadius: '8px',
        padding: '0.5rem 0.7rem',
        fontSize: '0.75rem',
        lineHeight: 1.45,
        color: 'var(--text-primary)',
        whiteSpace: 'pre-wrap',
      }}
    >
      {tip.text}
    </div>
  );
}
